import {
  Button,
  Link as MuiLink,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LatLng } from 'shared/types';
import { useMapState } from 'state';
import { useAuthState } from 'state/useAuthState';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Capacitor } from '@capacitor/core';
import { Link } from 'react-router-dom';
import { CoordinatesListItem } from 'components/CoordinatesListItem';

type MapPinInfoProps = {
  coordinates: LatLng;
  slackChannel?: string;
  slackChannelId?: string | null;
};

const SLACK_TEAM_ID = 'T025E476A3A';

// Reference:
// - Web: https://api.slack.com/reference/deep-linking#app_channel
// - Native: https://api.slack.com/reference/deep-linking#open_a_channel
const getSlackChannelDeepLink = (channelId: string): string =>
  Capacitor.isNativePlatform()
    ? `slack://channel?team=${SLACK_TEAM_ID}&id=${channelId}`
    : `https://slack.com/app_redirect?team=${SLACK_TEAM_ID}&channel=${channelId}`;

export const MapPinInfo = (props: MapPinInfoProps): JSX.Element => {
  const { coordinates, slackChannel, slackChannelId } = props;
  const { t } = useTranslation();
  const {
    permissions: { isInternalUser, canReport },
  } = useAuthState();
  const { incidentMapState } = useMapState();

  const createRoutePathParams = new URLSearchParams({
    lat: coordinates.lat.toString(),
    lng: coordinates.lng.toString(),
    zoom: Math.floor(incidentMapState.zoom).toString() || '',
  });

  return (
    <List disablePadding>
      <CoordinatesListItem coordinates={coordinates} format="decimal" />
      <CoordinatesListItem coordinates={coordinates} format="DDM" />
      <CoordinatesListItem coordinates={coordinates} format="DMS" />

      {isInternalUser && slackChannel && slackChannelId && (
        <ListItemButton
          divider
          component={MuiLink}
          href={getSlackChannelDeepLink(slackChannelId)}
          target="_blank"
        >
          <ListItemText
            primary={
              <>
                <Typography
                  color="secondary"
                  component="span"
                  variant="h4"
                  // eslint-disable-next-line i18next/no-literal-string
                >
                  Slack
                </Typography>
                <Typography
                  sx={{
                    color: 'accent.main',
                    fontWeight: 'fontWeightMedium',
                    textDecoration: 'underline',
                  }}
                >{`#${slackChannel}`}</Typography>
              </>
            }
          />
          <OpenInNewIcon />
        </ListItemButton>
      )}

      {(isInternalUser || canReport) && (
        <ListItem sx={{ py: 2 }}>
          <Button
            size="large"
            fullWidth
            component={Link}
            to={`/incident/create?${createRoutePathParams.toString()}`}
          >
            {t('mapPin.info.createIncident')}
          </Button>
        </ListItem>
      )}
    </List>
  );
};
